/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { ApolloError } from "@apollo/client";
import { SVGIcon } from "@nordcloud/gnui";
import { Provider } from "~/generated/graphql";
import { AccountStatus } from "~/tools";

export type FiltersProp = {
  account?: string[];
  accountExclude?: boolean;
  application?: string[];
  applicationExclude?: boolean;
  environment?: string[];
  provider?: Provider[];
  providerExclude?: boolean;
  costFrom?: number;
  costTo?: number;
  costExclude?: boolean;
  region?: string[];
  regionExclude?: boolean;
  typeSubtype?: string[];
  typeSubtypeExclude?: boolean;
  category?: string[];
  categoryExclude?: boolean;
  unassigned?: boolean;
  waste?: boolean;
  wastePolicy?: string[];
  wastePolicyExclude?: boolean;
  co2eWaste?: boolean;
  externalIntegration?: string[];
  externalIntegrationExclude?: boolean;
  tags?: string[];
  tagsExclude?: boolean;
  enrichment?: boolean;
  enrichmentIntegrationId?: string[];
  metadata?: string[];
  metadataExclude?: boolean;
  assignmentUpdatedFrom?: string;
  assignmentUpdatedTo?: string;
  assignmentUpdateExclude?: boolean;
  firstSeenAtFrom?: string;
  firstSeenAtExclude?: boolean;
  firstSeenAtTo?: string;
  lastSeenAtFrom?: string;
  lastSeenAtTo?: string;
  lastSeenAtExclude?: boolean;
  lifeSpanFrom?: number;
  lifeSpanTo?: number;
  lifeSpanExclude?: boolean;
  billingPeriod?: BillingPeriod;
};

export type ApplicationOption = {
  id: string;
  nid: string;
  name: string;
  environments: EnvironmentOption[];
};

export type CloudWasteOption = {
  category: string;
  categoryName: string;
  policies: PolicyOption[];
};

export type PolicyOption = {
  id: string;
  name: string;
};

export type EnvironmentOption = {
  id: string;
  nid: string;
  name: string;
  displayName?: string;
};

export type FiltersOptions = {
  accountsOptions: {
    value: string;
    label: string;
    status: AccountStatus;
  }[];
  regionOptions: {
    value: string;
    label: string;
  }[];
  resourceTypes: {
    value: string;
    label: string;
  }[];
  categories: {
    value: string;
    label: string;
  }[];
  cloudWastePolicies: {
    value: string | null | undefined;
    label: string | null | undefined;
  }[];
  cloudWastePoliciesCategorised: {
    category: string;
    categoryName: string;
    policies: {
      id: string;
      name: string;
    }[];
  }[];
  externalIntegrations: {
    value: string;
    label: string;
  }[];
  error: ApolloError | undefined;
  loadingExternalIntegrations: boolean;
  loadingCategories: boolean;
  loadingCloudWaste: boolean;
  loadingResourceTypes: boolean;
  loadingAccounts: boolean;
  loadingCloudWastePoliciesCategorised: boolean;
};

export type PodStatus = {
  status: string;
  icon: React.ComponentProps<typeof SVGIcon>["name"];
  tooltipStatus?: "danger" | "notification" | "success" | "warning";
};

export type FilterProps = {
  filters: FiltersProp;
  setFilters: (input: FiltersProp) => void;
};

export enum BillingPeriod {
  Current = "CURRENT",
  Previous = "PREVIOUS",
}
